import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Modal } from "@mui/material";
import { Typography } from "@material-ui/core";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ClientModal from "./ClientModal";
import ClientModel from "../../model/client";
import CloseIcon from "@mui/icons-material/Close";
import Misc from "../../utils/Misc";
import useStyles from "./styles";
import { PrimaryColor } from "../../shared/styles/colors";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

interface Props {
  client: ClientModel;
  resetList?: () => void;
  onOpen?: (id?: string) => void;
  onClose?: () => void;
}

const ClientDetailModal = ({ client, resetList, onOpen, onClose }: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOpen = () => {
    setOpen(true);
    if (onOpen) {
      onOpen(client.id?.toString());
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const clientId = searchParams.get("clientId");
    if (!open && clientId && clientId === client.id?.toString()) {
      handleOpen();
    }
  }, [searchParams]);

  return (
    <div>
      <Button onClick={handleOpen}>
        <MoreVertIcon sx={{ color: PrimaryColor}}/>
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        <Box sx={style}>
          <div className={classes.headerDiv}>
            <Typography className={classes.title}>Client info</Typography>
            <div className={classes.headerSubDiv}>
              <ClientModal client={client} resetList={resetList} />
              <Button onClick={handleClose} sx={{ color: "black" }}>
                <CloseIcon />
              </Button>
            </div>
          </div>
          <hr />
          <div className={classes.form}>
            <div className={classes.inputDiv}>
              <Typography className={classes.label}>Name</Typography>
              <Typography
                className={classes.inputDetail}
              >{client.firstName} {client.lastName}</Typography>
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>Email</Typography>
                <Typography className={classes.inputDetail}>
                  {client.email}
                </Typography>
              </div>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>Phone</Typography>
                <Typography className={classes.inputDetail}>
                  {Misc.formatPhoneNumber(client.phone)}
                </Typography>
              </div>
            </div>
            {client.street && (
              <>
                <div className={classes.inputGroup}>
                  <div className={classes.inputDiv}>
                    <Typography className={classes.label}>Street</Typography>
                    <Typography className={classes.inputDetail}>
                      {client.street}
                    </Typography>
                  </div>
                  <div className={classes.inputDiv}>
                    <Typography className={classes.label}>ZIP</Typography>
                    <Typography className={classes.inputDetail}>
                      {client.zip}
                    </Typography>
                  </div>
                </div>
                <div className={classes.inputGroup}>
                  <div className={classes.inputDiv}>
                    <Typography className={classes.label}>City</Typography>
                    <Typography className={classes.inputDetail}>
                      {client.city}
                    </Typography>
                  </div>
                  <div className={classes.inputDiv}>
                    <Typography className={classes.label}>State</Typography>
                    <Typography className={classes.inputDetail}>
                      {client.state}
                    </Typography>
                  </div>
                </div>
              </>
            )}
            <div className={classes.inputGroup}>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>Client Type</Typography>
                <Typography className={classes.inputDetail}>
                  {client?.ClientType?.name}
                </Typography>
              </div>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>Client Level</Typography>
                <Typography className={classes.inputDetail}>
                  {client?.ClientLevel?.name}
                </Typography>
              </div>
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>
                  Alternative Name
                </Typography>
                <Typography className={classes.inputDetail}>
                  {client?.alternativeName}
                </Typography>
              </div>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>
                  Alternative Phone
                </Typography>
                {client.alternativePhone ? (
                  <Typography className={classes.inputDetail}>
                    {Misc.formatPhoneNumber(client.alternativePhone)}
                  </Typography>
                ) : (
                  <Typography className={classes.inputDetail}>N/A</Typography>
                )}
              </div>
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>
                  Alternative Email
                </Typography>
                <Typography className={classes.inputDetail}>
                  {client?.alternativeEmail}
                </Typography>
              </div>
            </div>
            <Typography className={classes.label}>Notes</Typography>
            <textarea
              className={classes.inputDetail}
              value={client?.notes}
              disabled
            />
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default ClientDetailModal;
